import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => { data.append(key, value.toString()); });
    try {
      await fetch('https://readdy.ai/api/form/d61gl0n4klnl5oj67o2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setIsSuccess(true);
      form.reset();
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => { setIsModalOpen(false); setIsSuccess(false); };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d2849d89fb2bcc7e53a4d42f0cdeee32.jpg"
          alt="Business Turnaround Consulting"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/40"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              <span className="text-orange-300 text-sm font-medium">Business Turnaround Specialists</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6">
              We Help Struggling Businesses Find Their Way Forward
            </h1>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
              JBS Consulting is the trusted partner for distressed companies. We deliver cost-effective turnaround strategies, cash flow solutions, and operational restructuring.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Get a Free Assessment
                <i className="ri-arrow-right-line"></i>
              </button>
              <Link
                to="/services"
                className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-slate-900 transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <button onClick={closeModal} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
            <div className="p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Request Received!</h3>
                  <p className="text-gray-600 mb-6">Thank you for reaching out. A JBS Consulting advisor will contact you within 24 hours to discuss your situation.</p>
                  <button onClick={closeModal} className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">Done</button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Request a Free Business Assessment</h3>
                    <p className="text-gray-600">Tell us about your situation — we'll reach out within 24 hours.</p>
                  </div>
                  <form id="consultation-booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input type="text" name="firstName" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm" placeholder="John" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input type="text" name="lastName" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm" placeholder="Doe" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm" placeholder="john@company.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input type="tel" name="phone" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm" placeholder="(555) 123-4567" />
                      <div className="mt-2 flex items-start gap-2">
                        <input type="checkbox" name="smsConsent" id="hero-sms-consent" required className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer flex-shrink-0" />
                        <label htmlFor="hero-sms-consent" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                          By providing your phone number, you consent to receive calls and SMS text messages from JBS Consulting, LLC regarding your inquiry. Message &amp; data rates may apply. Reply STOP to opt out at any time. View our <a href="/privacy-policy" className="underline hover:text-orange-500 transition-colors">Privacy Policy</a>.
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                      <input type="text" name="company" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm" placeholder="Your Company LLC" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Primary Challenge</label>
                      <select name="service" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                        <option value="">Select your situation</option>
                        <option value="Cash Flow Crisis">Cash Flow Crisis</option>
                        <option value="Business Turnaround">Business Turnaround</option>
                        <option value="Creditor Negotiations">Creditor Negotiations</option>
                        <option value="Litigation Support">Litigation Support</option>
                        <option value="Capital Solutions">Capital Solutions</option>
                        <option value="Interim Management">Interim Management (CXO)</option>
                        <option value="General Assessment">General Assessment</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Brief Description (Optional)</label>
                      <textarea name="notes" rows={3} maxLength={500} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm resize-none" placeholder="Briefly describe your business challenges..."></textarea>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {isSubmitting ? (<><i className="ri-loader-4-line animate-spin"></i>Submitting...</>) : (<>Request Free Assessment<i className="ri-arrow-right-line"></i></>)}
                    </button>
                  </form>
                  <p className="text-xs text-gray-500 text-center mt-4">Your information is confidential. We never share your data.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
